import { useCallback } from 'react';

import { Button } from '@braskai/ui-kit';

import { ActionButtonTitle } from '@/widgets/pricing/pricingCards/lib/enums';

import { useBalanceStore } from '@/entities/balance';
import { BOOK_SALES_DEMO_SOURCES, useOpenBookSalesDemo } from '@/entities/demoBooking';
import { getPricingActionLabel, useProducts } from '@/entities/pricing';
import { useCurrentUser, useIsPaidTrial } from '@/entities/user/currentUser';

import { usePricingAnalyticsProperties } from '@/shared/contexts/PricingAnalyticsContext';
import { useInterfaceLanguage } from '@/shared/i18n';
import { isBusiness250Plan } from '@/shared/lib/billing/isBusiness250Plan';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Plan, PlanCategory, Price } from '@/shared/types/Billing';

import { ActionButton } from './styled';
import { getButtonTitle } from './utils/getButtonTitle';
import { getMixpanelButtonEvent } from './utils/getMixpanelButtonEvent';

type CardButtonProps = {
  chosenPlanId: null | string;
  isActiveSubscription: boolean;
  isCustomPlan: boolean;
  isPriceActive: boolean;
  plan: Plan;
  priceId: string | undefined;
  onActionButtonClick: (priceId: Price['id'], plan: Plan) => void;
};

export const CardButton = ({
  chosenPlanId,
  isActiveSubscription,
  isCustomPlan,
  isPriceActive,
  plan,
  priceId,
  onActionButtonClick,
}: CardButtonProps) => {
  const {
    hasCanceledSubscription,
    hasEnterprisePlan,
    hasExpiredSubscription,
    hasPausedSubscription,
    hasSubscriptionCancelAtPeriodEnd,
    hasTrialingEnterprisePlan,
    hasTrialPlan,
    planCategory,
  } = useBalanceStore();

  const analyticsProperties = usePricingAnalyticsProperties();
  const { products } = useProducts();
  const { t } = useInterfaceLanguage();
  const { data: user } = useCurrentUser();
  const isPaidTrial = useIsPaidTrial(user?.subscription) === true && !hasEnterprisePlan;
  const openBookSalesDemo = useOpenBookSalesDemo();

  const { id: cardPlanId, name: planName } = plan || {};
  const activePrice = plan?.prices.find((p) => p.id === priceId);
  const subscription = activePrice?.recurring?.interval === PaymentInterval.Month ? 'monthly' : 'annual';
  const userPriceData = user?.subscription?.plan;

  const isPlanActive = Boolean(
    userPriceData && userPriceData.product_id === cardPlanId && !hasCanceledSubscription && !hasExpiredSubscription,
  );

  const isPurchaseInProgress = Boolean(chosenPlanId);
  const isCurrentPlanLoading = chosenPlanId === priceId;

  const buttonTitle = getButtonTitle({
    hasCanceledSubscription,
    hasExpiredSubscription,
    hasPausedSubscription,
    hasSubscriptionCancelAtPeriodEnd,
    hasTrialingEnterprisePlan,
    hasTrialPlan,
    isPaidTrial,
    isPlanActive,
    isPriceActive,
    plan,
    plans: products?.plan,
    subscription: user?.subscription,
  });

  const isDowngrade = buttonTitle === ActionButtonTitle.Downgrade;
  const isSwitchToAnnualPlan = buttonTitle === ActionButtonTitle.SwitchToAnnual;
  const isUpgrade = buttonTitle === ActionButtonTitle.Upgrade;

  const isCreatorProPlan = plan.category === PlanCategory.CreatorPro;

  const hasBusinessSubscription = planCategory === PlanCategory.Business;

  const isBusinessContactUs =
    plan.category === PlanCategory.Business && !hasBusinessSubscription && !isBusiness250Plan(plan);

  const handleGetStartedClick = useCallback(() => {
    if (!priceId) {
      return;
    }

    const sendEvent = getMixpanelButtonEvent({
      analyticsProperties,
      category: plan.category,
      isDowngradePlan: isDowngrade,
      isSwitchToAnnualPlan,
      isUpgradePlan: isUpgrade,
      planName,
      subscription,
    });

    sendEvent?.();

    onActionButtonClick(priceId, plan);
  }, [
    analyticsProperties,
    priceId,
    plan,
    onActionButtonClick,
    planName,
    isUpgrade,
    isSwitchToAnnualPlan,
    isDowngrade,
    subscription,
  ]);

  const handleEnterpriseLinkClick = () => {
    if (isPurchaseInProgress) {
      return;
    }

    MixpanelEvents.enterpriseButtonClick();
    openBookSalesDemo(
      BOOK_SALES_DEMO_SOURCES.pricingEnterpriseCard,
      'Contact us',
      analyticsProperties.pricing_entry_point,
    );
  };

  if (isCustomPlan) {
    return (
      <Button
        data-testid="card-button-contact-us"
        disabled={isPurchaseInProgress}
        size="sm"
        sx={{ backgroundColor: 'common.white', color: 'primary.900', width: '100%' }}
        variant="outlined"
        onClick={handleEnterpriseLinkClick}
      >
        {t('pricing.actions.contactUs')}
      </Button>
    );
  }

  if (isBusinessContactUs) {
    return (
      <ActionButton
        data-testid="card-button-business-contact-us"
        disabled={isPurchaseInProgress}
        isTransparent={!(isUpgrade && isActiveSubscription)}
        onClick={() =>
          openBookSalesDemo(
            BOOK_SALES_DEMO_SOURCES.pricingBusinessCard,
            'Contact us',
            analyticsProperties.pricing_entry_point,
          )
        }
      >
        {t('pricing.actions.contactUs')}
      </ActionButton>
    );
  }

  return (
    <ActionButton
      data-testid="card-button-get-started-click"
      disabled={isPurchaseInProgress}
      isLoading={isCurrentPlanLoading}
      isTransparent={isUpgrade && isActiveSubscription ? false : isDowngrade || !isCreatorProPlan}
      onClick={handleGetStartedClick}
    >
      {getPricingActionLabel(t, buttonTitle)}
    </ActionButton>
  );
};
